
import { format, isValid, parseISO } from "date-fns";
import { da } from "date-fns/locale/da";
import { enUS } from "date-fns/locale/en-US";
import i18next from "i18next";
import { safeParseDate } from "@/utils/dateValidation";

export const formatDate = (dateString: string | null | undefined, useDanishFormat = false): string => {
  if (!dateString) return useDanishFormat ? "Dato ikke angivet" : "Date not specified";
  
  const locale = useDanishFormat || i18next.language === "da" ? da : enUS;
  
  try {
    // First try our safe parser that handles various formats
    const parsedDate = safeParseDate(dateString);
    if (parsedDate && isValid(parsedDate)) {
      return format(parsedDate, "d. MMMM yyyy", { locale });
    }
    
    // If that fails, try standard parsers - but these should be caught by safeParseDate already
    
    // Handle ISO date string
    if (typeof dateString === 'string' && (dateString.includes('T') || dateString.includes('Z'))) {
      try {
        const date = parseISO(dateString);
        if (isValid(date)) {
          return format(date, "d. MMMM yyyy", { locale });
        }
      } catch (e) {
        console.warn(`Error parsing ISO date: ${dateString}`, e);
      }
    }
    
    // Handle YYYYMMDD format commonly used in TED data
    if (typeof dateString === 'string' && /^\d{8}$/.test(dateString)) {
      try {
        const year = dateString.substring(0, 4);
        const month = dateString.substring(4, 6);
        const day = dateString.substring(6, 8);
        
        const date = new Date(`${year}-${month}-${day}`);
        if (isValid(date)) {
          return format(date, "d. MMMM yyyy", { locale });
        }
      } catch (e) {
        console.warn(`Error parsing YYYYMMDD date: ${dateString}`, e);
      }
    }
    
    // Handle YYYY-MM-DD format
    if (typeof dateString === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      try {
        const date = parseISO(dateString);
        if (isValid(date)) {
          return format(date, "d. MMMM yyyy", { locale });
        }
      } catch (e) {
        console.warn(`Error parsing YYYY-MM-DD date: ${dateString}`, e);
      }
    }
    
    return String(dateString);
  } catch (error) {
    console.error("Error formatting date:", error);
    return typeof dateString === 'string' ? dateString : "Invalid date";
  }
};

export const formatTime = (timeString: string): string => {
  if (!timeString) return "Time not specified";

  try {
    // Handle ISO datetime string
    if (timeString.includes('T')) {
      const date = parseISO(timeString);
      if (isValid(date)) {
        return format(date, "p");
      }
    }
    
    return timeString;
  } catch (error) {
    console.error("Error formatting time:", error);
    return timeString;
  }
};

export const formatDateTime = (dateTimeString: string | null | undefined, useDanishFormat = false): string => {
  if (!dateTimeString) return useDanishFormat ? "Dato ikke angivet" : "Date/time not specified";
  
  const locale = useDanishFormat || i18next.language === "da" ? da : enUS;

  try {
    // First try our safe parser that handles various formats
    const parsedDate = safeParseDate(dateTimeString);
    if (parsedDate && isValid(parsedDate)) {
      return format(parsedDate, "d. MMMM yyyy 'kl.' HH:mm", { locale });
    }
    
    // Handle ISO datetime string
    if (typeof dateTimeString === 'string' && (dateTimeString.includes('T') || dateTimeString.includes('Z'))) {
      try {
        const date = parseISO(dateTimeString);
        if (isValid(date)) {
          return format(date, "d. MMMM yyyy 'kl.' HH:mm", { locale });
        }
      } catch (e) {
        console.warn(`Error parsing ISO date: ${dateTimeString}`, e);
      }
    }
    
    return String(dateTimeString);
  } catch (error) {
    console.error("Error formatting date/time:", error);
    return typeof dateTimeString === 'string' ? dateTimeString : "Invalid date";
  }
};

export const formatCurrency = (value: any, currency: string = "EUR"): string => {
  if (value === undefined || value === null) return "Not specified";
  
  const numValue = parseFloat(value);
  if (isNaN(numValue)) return `${value} ${currency}`;
  
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(numValue);
  } catch (error) {
    console.error("Error formatting currency:", error);
    return `${value} ${currency}`;
  }
};

export const getMainDeadline = (tender: any): string | null => {
  if (!tender) return null;
  
  // First priority: Use DT field as the main deadline
  if (Array.isArray(tender.DT) && tender.DT.length > 0) {
    return tender.DT[0];
  }
  
  // Second priority: Use deadline-receipt-tender-date-lot field
  if (Array.isArray(tender["deadline-receipt-tender-date-lot"]) && tender["deadline-receipt-tender-date-lot"].length > 0) {
    return tender["deadline-receipt-tender-date-lot"][0];
  }
  
  // Third priority: Use deadline-date-lot field
  if (Array.isArray(tender["deadline-date-lot"]) && tender["deadline-date-lot"].length > 0) {
    return tender["deadline-date-lot"][0];
  }
  
  // Fourth priority: Use deadline field (but this is often the additional info deadline rather than the tender submission deadline)
  if (Array.isArray(tender.deadline)) {
    return tender.deadline[0];
  } else if (typeof tender.deadline === 'string') {
    return tender.deadline;
  }
  
  // Fifth priority: Use deadline_date field (from older format)
  if (tender.deadline_date) {
    return tender.deadline_date;
  }
  
  return null;
};

export const getAdditionalInfoDeadline = (tender: any): string | null => {
  if (!tender) return null;
  
  // Use deadline field specifically for additional info
  if (Array.isArray(tender.deadline) && tender.deadline.length > 0) {
    return tender.deadline[0];
  } else if (typeof tender.deadline === 'string') {
    return tender.deadline;
  }
  
  return null;
};

export const isProcedureOpen = (tender: any): boolean => {
  if (!tender) return true; // Default to open procedure
  
  // Check the PR field which contains the procedure type
  if (tender.PR === 'open') {
    return true;
  } else if (tender.PR === 'restricted' || tender.PR === 'negotiated') {
    return false;
  }
  
  // Default to open procedure if PR is not specified
  return true;
};
